import AboutUsImageAndText from 'components/about-us/AboutUsImageAndText';
import AboutUsModule from 'components/about-us/AboutUsModule';
import AboutUsSiteContainer from 'components/about-us/AboutUsSiteContainer';
import ExpandySection from 'components/ExpandySection';
import ImageWithFallback from 'components/ImageWithFallback';
import MainLayout from 'components/layout/MainLayout';
import RoundedButton from 'components/RoundedButton';
import { useDrawStore } from 'hooks/stores/useDrawStore';
import { NextSeo } from 'next-seo';
import ChevronLeft15 from '../components/svg/ChevronLeft15.svg';
import styles from './giving-back.module.css';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';

const reasonsList = [
  {
    buttonText: 'Carbon',
    headingText: 'Reducing CO2 Levels Helps Mitigate Climate Change',
    content: (
      <p>
        Monitoring carbon in forest restoration is vital as forests act as the
        planet's lungs, absorbing carbon dioxide and releasing oxygen. By
        tracking carbon sequestration, we can determine the efficacy of
        restoration efforts in mitigating climate change. It allows for a
        quantifiable measure of a forest's contribution to global carbon cycles
        and offers insights into the potential of forests in offsetting
        anthropogenic carbon emissions.
      </p>
    ),
    imgSrc:
      'https://daymade-website-assets.s3.eu-west-1.amazonaws.com/2026/giving-back/cloudy-trees.png',
  },
  {
    buttonText: 'Biodiversity',
    headingText: 'Encouraging Growth of Native Species Supports Wildlife',
    content: (
      <p>
        Biodiversity is the foundation of ecosystem resilience and
        functionality. Monitoring biodiversity ensures that restoration efforts
        are promoting a rich variety of species and not inadvertently creating
        monocultures. A biodiverse forest is more resilient to pests, diseases,
        and changing climatic conditions. Furthermore, each species plays a
        unique role in the ecosystem, and by monitoring biodiversity, we can
        better understand and protect these intricate relationships. veritree
        uses a mixture of technologies to monitor biodiversity including
        environmental DNA sampling, acoustic monitoring and remote sensing.
      </p>
    ),
    imgSrc:
      'https://daymade-website-assets.s3.eu-west-1.amazonaws.com/2026/giving-back/zebras.png',
  },
  {
    buttonText: 'People',
    headingText: 'Job Creation Empowers Communities',
    content: (
      <p>
        People are central to ecosystem restoration. From smallholder farmers to
        local and Indigenous peoples, to employees in forest enterprises,
        roughly 1.6 billion people live in and around forests and depend on its
        natural resources to some extent. Restoration is an opportunity to
        achieve equity in both rural and urban areas, providing avenues to
        empower women and young people. Moreover, by promoting and conserving
        healthy landscapes, restoration can help promote the use of traditional
        knowledge and protect Indigenous cultural systems and practices.
      </p>
    ),
    imgSrc:
      'https://daymade-website-assets.s3.eu-west-1.amazonaws.com/2026/giving-back/field.png',
  },
  {
    buttonText: 'Land',
    headingText: 'Replenishing Deforested Areas Boost Environmental Stability',
    content: (
      <p>
        Monitoring land impacts in forest restoration helps ensure that the
        restored areas are resilient to degradation, such as soil erosion and
        desertification. Healthy forests improve soil fertility, structure, and
        prevent land degradation, promoting a sustainable loop of growth and
        regeneration. Moreover, tracking land changes can reveal any
        encroachments or unplanned land-use changes, ensuring that restoration
        efforts remain undisturbed and effective.
      </p>
    ),
    imgSrc:
      'https://daymade-website-assets.s3.eu-west-1.amazonaws.com/2026/giving-back/more-trees.png',
  },
  {
    buttonText: 'Water',
    headingText: 'Rebuilding Natural Habitats Maintain Healthy Ecosystems',
    content: (
      <p>
        Forests play a crucial role in maintaining hydrological cycles.
        Monitoring water aspects ensures that restored forests are aiding in
        groundwater recharge, regulating water flows, and maintaining water
        quality by filtering pollutants. Forests also act as buffers during
        heavy rainfall, preventing floods and reducing soil erosion. Hence,
        keeping an eye on water dynamics in and around forests ensures both
        ecosystem health and human wellbeing.
      </p>
    ),
    imgSrc:
      'https://daymade-website-assets.s3.eu-west-1.amazonaws.com/2026/giving-back/tree-roots.png',
  },
];

const resultBulletPoints = [
  {
    bigText: '6M+',
    text: 'trees planted',
    imageSrc:
      'https://daymade-website-assets.s3.eu-west-1.amazonaws.com/2026/giving-back/icon-tree.png',
  },
  {
    bigText: '3',
    text: 'countries',
    imageSrc:
      'https://daymade-website-assets.s3.eu-west-1.amazonaws.com/2026/giving-back/icon-earth.png',
  },
  {
    bigText: '3',
    text: 'active smart forests',
    imageSrc:
      'https://daymade-website-assets.s3.eu-west-1.amazonaws.com/2026/giving-back/icon-trees.png',
  },
  {
    bigText: '6K+',
    text: 'farming families empowered',
    imageSrc:
      'https://daymade-website-assets.s3.eu-west-1.amazonaws.com/2026/giving-back/icon-hands.png',
  },
];

const GivingBack = observer(() => {
  const { treesPlantedCount } = useDrawStore();
  const [reasonIndex, setReasonIndex] = useState(0);
  const selectedReason = reasonsList[reasonIndex];

  return (
    <MainLayout limitItemWidths>
      <NextSeo
        title="Plant Trees | Win Epic Prizes | DAYMADE"
        description="Win Epic Prizes. From beauty boxes to brunch in Bali for only £2.95"
        openGraph={{
          title: 'Plant Trees | Win Epic Prizes | DAYMADE',
          description:
            'Win Epic Prizes. From beauty boxes to brunch in Bali for only £2.95',
        }}
      />

      <div className="aboutUsTypographyReset">
        <AboutUsModule background="white" withNoPadding>
          <div className={styles.heroContainer}>
            <video className={styles.heroVideo} autoPlay muted loop>
              <source src="https://daymade-website-assets.s3.eu-west-1.amazonaws.com/2026/giving-back/treeheader.mp4" />
            </video>
            <img
              className={styles.heroBadge}
              src="https://daymade-website-assets.s3.eu-west-1.amazonaws.com/2026/giving-back/make-a-purchase-plant-a-tree.svg"
              alt="Make a purchase, plant a tree"
            />
          </div>
        </AboutUsModule>
        <AboutUsModule background="ecoGreenGradient" withReducedPadding>
          <AboutUsSiteContainer>
            <p className="aboutUsTypographyBodySmall textAlignCenter">
              For every DAYMADE purchase you make, we plant a tree with{' '}
              <strong>veritree</strong>
            </p>
            <p className="aboutUsTypographyHeaderXlargeOnTeal textAlignCenter noMargin">
              {treesPlantedCount?.toLocaleString('en-GB')}{' '}
              <span
                className={'aboutUsTypographyBodySmall ' + styles.treesSubtext}
              >
                trees planted to date
              </span>
            </p>
          </AboutUsSiteContainer>
        </AboutUsModule>
        <AboutUsModule background="white" withReducedPadding>
          <AboutUsSiteContainer>
            <ExpandySection
              expandedContainerClassName={styles.expanded}
              headerContent={
                <div
                  className={
                    'aboutUsTypographyBodySmall ' +
                    styles.ourCertificatesHeading
                  }
                >
                  <ImageWithFallback
                    className={styles.ourCertificatesIcon}
                    src="https://daymade-website-assets.s3.eu-west-1.amazonaws.com/2026/giving-back/document.png"
                    width={15}
                    height={20}
                    alt=""
                  />
                  Our certificates
                  <ChevronLeft15 className={styles.ourCertificatesChevron} />
                </div>
              }
            >
              <div
                className={
                  styles.sideBySideImages + ' ' + styles.ourCertificatesImages
                }
              >
                <a
                  href="https://daymade-website-assets.s3.eu-west-1.amazonaws.com/2026/giving-back/Planting%20Certificate%2010-24%20to%2004-26.pdf"
                  target="_blank"
                >
                  <ImageWithFallback
                    src="https://daymade-website-assets.s3.eu-west-1.amazonaws.com/2026/giving-back/Planting%20Certificate%2010-24%20to%2004-26.jpg"
                    width={828}
                    height={640}
                    alt="DAYMADE's veritree certificate of recognition"
                  />
                </a>
                <ImageWithFallback
                  src="https://daymade-website-assets.s3.eu-west-1.amazonaws.com/2026/giving-back/trees-org-certificate.png"
                  width={792}
                  height={612}
                  alt="DAYMADE's trees.org certificate of support"
                />
                <ImageWithFallback
                  src="https://daymade-website-assets.s3.eu-west-1.amazonaws.com/2026/giving-back/eden-reforestation-projects-certificate.png"
                  width={2500}
                  height={1930}
                  alt="DAYMADE's Eden Reforestation projects certificate"
                />{' '}
              </div>
            </ExpandySection>
            <hr className={styles.ourCertificatesHr} />
          </AboutUsSiteContainer>
        </AboutUsModule>
        <AboutUsModule background="white" withReducedPadding>
          <AboutUsSiteContainer>
            <h2
              className={
                'aboutUsTypographyHeaderXlargeOnWhite aboutUsTypographyAltUnderlineColour ' +
                styles.verifiableTreesHeader
              }
              data-text="Together we are planting verifiable trees."
            >
              Together we are planting verifiable trees.
            </h2>
            <AboutUsImageAndText
              background={'white'}
              videoSrc="https://daymade-website-assets.s3.eu-west-1.amazonaws.com/2026/giving-back/treeavicenniamarina.mp4"
              videoPoster="https://daymade-website-assets.s3.eu-west-1.amazonaws.com/2026/giving-back/treeavicenniamarina-poster.jpg"
            >
              <p className="aboutUsTypographyHeaderMedium">Meet veritree</p>
              <p>
                We've partnered with{' '}
                <a href="https://impact.veritree.com/daymade">veritree</a> to
                support verified, high-impact tree planting through two local
                planting partners:{' '}
                <a
                  target="_blank"
                  rel="noopener noreferrer"
                  href="https://earthlungsreforestation.org/"
                >
                  EarthLungs
                </a>{' '}
                in Kenya and Tanzania, and Mother Trees in Senegal. With every
                purchase made on our site, trees are planted to restore degraded
                ecosystems, uplift local communities, and create lasting
                environmental impact.{' '}
                <a href="https://impact.veritree.com/daymade">veritree</a>{' '}
                tracks and verifies reforestation projects using advanced
                technology and on-the-ground monitoring, ensuring transparency
                and trust. Together with their partners, veritree has planted
                over 55 million trees since inception.
              </p>
              <p className={styles.buttonContainer}>
                <RoundedButton
                  rebrandDm2025
                  colorStyle="OutlineDark"
                  href="https://impact.veritree.com/daymade"
                >
                  Discover Veritree
                </RoundedButton>
              </p>
            </AboutUsImageAndText>

            <div
              className={
                styles.sideBySideImages +
                ' ' +
                styles.sideBySideImagesWithRoundedCorners
              }
            >
              <ImageWithFallback
                src="https://daymade-website-assets.s3.eu-west-1.amazonaws.com/2026/giving-back/daymade-veritree-1.png"
                width={2048}
                height={1331}
                alt="People planting trees for DAYMADE"
              />
              <ImageWithFallback
                src="https://daymade-website-assets.s3.eu-west-1.amazonaws.com/2026/giving-back/daymade-veritree-2.png"
                width={2048}
                height={1336}
                alt="A close up of a tree sapling"
              />
            </div>

            <AboutUsImageAndText
              images={[
                {
                  imagePath:
                    'https://daymade-website-assets.s3.eu-west-1.amazonaws.com/2026/giving-back/tree-landscape.png',
                  imageAlt: 'A landscape view of a tree planting area',
                },
              ]}
              imageOverlay={
                <div className={styles.globeOverlayContainer}>
                  <ImageWithFallback
                    layout="fill"
                    objectFit="contain"
                    src="https://daymade-website-assets.s3.eu-west-1.amazonaws.com/2026/giving-back/globe.png"
                    alt=""
                  />
                </div>
              }
              background={'white'}
              imageOnRight
              header={
                <>
                  <p className="aboutUsTypographyHeaderMedium">
                    Where are the trees planted?
                  </p>
                  <p>
                    Our trees are planted in three locations: Kenya, Tanzania
                    and the Gambia River Watershed region in Senegal. In Kenya
                    and Tanzania, EarthLungs works with local
                    communities—employing nearly the entire population of a
                    nearby village—to restore mangrove estuaries and enhance
                    biodiversity. In Senegal, our partnership with Mother Trees
                    supports agroforestry initiatives that restore degraded
                    farmland, empower over 6,000 farming families (many of them
                    women), and promote food security through sustainable,
                    community-led practices. Together, these projects not only
                    heal ecosystems but also generate meaningful, lasting
                    benefits for the people who depend on them. Have a look at
                    the impact the DAYMADE community has created so far.
                  </p>
                  <p className={styles.buttonContainer}>
                    <RoundedButton
                      rebrandDm2025
                      colorStyle="OutlineDark"
                      href="https://impact.veritree.com/daymade"
                    >
                      Explore now
                    </RoundedButton>
                  </p>
                </>
              }
            >
              {null}
            </AboutUsImageAndText>
          </AboutUsSiteContainer>
        </AboutUsModule>
        <AboutUsModule background="grey">
          <AboutUsSiteContainer>
            <h2 className="aboutUsTypographyHeaderXlargeOnWhite aboutUsTypographyAltUnderlineColour">
              The results
            </h2>
            <p className="textAlignCenter">
              A look at what veritree have achieved so far
            </p>
            <ul className={styles.resultsList}>
              {resultBulletPoints.map((x, index) => (
                <li key={index} className={styles.resultsItem}>
                  <ImageWithFallback
                    src={x.imageSrc}
                    alt=""
                    height={70}
                    width={100}
                  />
                  <strong className="aboutUsTypographyHeaderMedium">
                    {x.bigText}
                  </strong>{' '}
                  <span>{x.text}</span>
                </li>
              ))}
            </ul>
            <p className="textAlignCenter aboutUsTypographyBodySmall noMargin">
              <em>*updated quarterly</em>
            </p>
          </AboutUsSiteContainer>
        </AboutUsModule>

        <AboutUsModule background="white">
          <AboutUsSiteContainer>
            <h2 className="aboutUsTypographyHeaderXlargeOnWhite aboutUsTypographyAltUnderlineColour">
              Why plant trees
            </h2>
            <p className="textAlignCenter">
              It’s about so much more. veritree measures impact.
            </p>
            <ul className={styles.tabBar}>
              {reasonsList.map((x, index) => (
                <li key={x.buttonText}>
                  <button
                    className={
                      styles.reasonButton +
                      ' ' +
                      (index === reasonIndex ? styles.reasonButtonSelected : '')
                    }
                    onClick={() => setReasonIndex(index)}
                  >
                    {x.buttonText}{' '}
                  </button>
                </li>
              ))}
            </ul>
            <div className={styles.reasonContainer}>
              <div className={styles.reasonImage}>
                <ImageWithFallback
                  src={selectedReason.imgSrc}
                  alt=""
                  layout="fill"
                  objectFit="cover"
                />
              </div>
              <div className={styles.reasonText}>
                <h3 className="aboutUsTypographyHeaderMedium">
                  {selectedReason.headingText}
                </h3>
                {selectedReason.content}
                <p className="noMargin">
                  <a href="https://impact.veritree.com/daymade">Learn more</a>
                </p>
              </div>
            </div>
          </AboutUsSiteContainer>
        </AboutUsModule>

        <AboutUsImageAndText
          images={[
            {
              imagePath:
                'https://daymade-website-assets.s3.eu-west-1.amazonaws.com/2026/giving-back/ecologi-logo.png',
              imageAlt: 'ecologi logo',
            },
          ]}
          background="ecoGreenGradient"
          header={
            <h2 className="aboutUsTypographyHeaderXlargeOnTeal">
              A holistic approach
            </h2>
          }
          objectFit="contain"
          imageSizeDesktop={200}
          imageNoMinHeight
        >
          <p>
            With DAYMADE, your subscription helps our holistic approach to
            sustainability. We work with a lot of planet-friendly partners and
            carbon off-set all our winners' flights as well as our team's carbon
            footprint through our UK-based partner{' '}
            <a href="https://ecologi.com/">Ecologi</a>
          </p>
          <p>
            <RoundedButton
              rebrandDm2025
              colorStyle="OutlineDark"
              href="https://ecologi.com/daymadeuk"
            >
              Find out more
            </RoundedButton>
          </p>
        </AboutUsImageAndText>
      </div>
    </MainLayout>
  );
});

export default GivingBack;
